import {
  ChangeEvent,
  ComponentProps,
  FC,
  RefObject,
  createRef,
  useCallback,
  useRef,
  useState,
} from 'react'

import {
  Box,
  Button,
  Card,
  Grid,
  Icon,
  SearchField,
  Text,
} from '@factorialco/deprecated-design-system'
import { PermissionsGetPermissionsQuery } from 'generated/resources/graphql'
import { sortBy } from 'lodash'

import { observer } from 'mobx-react'

import Separator from 'modules/core/components/Separator'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useTextSearch } from 'modules/core/hooks/useTextSearch'
import { track } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import Item from './Item'

type Permission = PermissionsGetPermissionsQuery['permissions']['permissions'][number]

interface Props {
  disabled: boolean
  permissionGroupId: string
  permissions: Permission[]
}

const List: FC<Props> = ({ disabled = false, permissionGroupId, permissions }) => {
  const session = useCurrentSession()
  const [search, setSearch] = useState('')
  const searching = search.trim().length > 0
  const containerRef = useRef<HTMLElement | null>(null)
  const onSearch = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value)
  }, [])

  const searchResults = useTextSearch(permissions, search, {
    fields: ['verbLabel', 'categoryLabel'],
    storeFields: ['id'],
    combineWith: 'AND',
    fuzzy: false,
  }).results

  function filterPermissions() {
    if (!searching) {
      return permissions
    }
    const matchedIds = new Set(searchResults.map((permission) => permission.id))
    return permissions.filter((permission) => matchedIds.has(permission.id))
  }

  const filteredPermissions = filterPermissions()

  let filteredCategories = buildCategories(filteredPermissions)
  filteredCategories = sortBy(filteredCategories, (category) => category.label.toLocaleLowerCase())

  const refs = filteredCategories.reduce<Record<string, RefObject<HTMLElement>>>((acc, value) => {
    acc[value.name] = createRef()
    return acc
  }, {})

  const handleCategoryClick = (name: string) => {
    track('Clicked category', {
      user_id: session.access.id,
      category: name,
    })
    const categoryElement = refs[name]?.current
    if (categoryElement) {
      const yPosition = categoryElement.getBoundingClientRect().top
      const offset = 130
      containerRef.current?.closest('[data-scroll-container]')?.scrollTo({
        top: yPosition - offset,
        behavior: 'smooth',
      })
    }
  }

  const ScrollToTopButton = () => {
    return (
      <Button
        type="secondary"
        onClick={() =>
          containerRef.current?.closest('[data-scroll-container]')?.scrollTo({
            top: 0,
            behavior: 'smooth',
          })
        }
      >
        {i18n.t('settings.permissions.group.scroll_to_the_top')}
      </Button>
    )
  }

  const categoriesCards = filteredCategories.map((category) => {
    return (
      <Card key={category.name} onClick={() => handleCategoryClick(category.name)}>
        <Card.Eyebrow>
          <Icon.Medium icon={category.icon} color="grey800" />
        </Card.Eyebrow>
        <Card.Title>{category.label}</Card.Title>
      </Card>
    )
  })

  return (
    <Box paddingX="s64" ref={containerRef}>
      <Box paddingTop="s32">
        <SearchField variant="big" search={search} onSearch={onSearch} placeholder={'Search'} />
      </Box>
      <Box padding="s40" alignSelf="center">
        <Text.Header2>
          {filteredPermissions.length > 0
            ? i18n.t('settings.permissions.group.all_permissions')
            : i18n.t('settings.permissions.group.no_permissions')}
        </Text.Header2>
      </Box>
      <Grid variant="medium">{categoriesCards}</Grid>
      <Box>
        {filteredCategories.map((category) => (
          <Box key={category.name}>
            <Box ref={refs[category.name]} alignItems="center" paddingTop="s32">
              <Box padding="s16">
                <Icon.Medium color="radicalRed1000" icon={category.icon} />
              </Box>
              <Text.Normal capitalize weight="medium">
                {category.label}
              </Text.Normal>
              <Separator />
            </Box>
            <Item
              disabled={disabled}
              permissions={category.permissions}
              permissionGroupId={permissionGroupId}
            />
            <Box key={category.name} alignItems="center" paddingTop="s32">
              <ScrollToTopButton />
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  )
}

interface Category {
  name: string
  label: string
  permissions: Permission[]
  icon: ComponentProps<typeof Icon.Medium>['icon']
}

function buildCategories(permissions: Permission[]): Category[] {
  const categories = new Map<string, Category>()
  for (const permission of permissions) {
    const category = categories.get(permission.category)
    if (category) {
      category.permissions.push(permission)
    } else {
      categories.set(permission.category, {
        name: permission.category,
        label: permission.categoryLabel,
        permissions: [permission],
        icon: findIcon(permission.category),
      })
    }
  }
  return [...categories.values()]
}

function findIcon(category: string): ComponentProps<typeof Icon.Medium>['icon'] {
  const icons: Record<string, ComponentProps<typeof Icon.Medium>['icon']> = {
    approvals: 'check-circle-line',
    attendance: 'shift-management',
    billing: 'file-filled',
    company: 'office',
    compensations: 'payroll',
    competencies: 'performance',
    contracts: 'contract',
    custom_fields: 'pencil',
    custom_tables: 'pencil',
    dashboard_events: 'dashboard',
    documents: 'archive-open',
    employees: 'manager',
    enps: 'comment',
    face_recognition: 'camera-plus',
    finance: 'money-bag',
    goals: 'target',
    job_catalog: 'briefcase',
    locations: 'pin',
    permissions_groups: 'lock-locked',
    processes: 'list',
    project_management: 'projects',
    reviews: 'feedback',
    shift_management: 'shift-management',
    software: 'layers-front',
    space_control: 'space-control',
    surveys: 'comment',
    teams: 'manager',
    time_planning: 'shift-management',
    timeoff: 'time-off',
    work_schedule: 'clock',
  }
  return (icons[category] ?? category) as ComponentProps<typeof Icon.Medium>['icon']
}

export default observer(List)
